// AGC: Mock Pricing and Financial Service for Lotes
// AGC: This simulates pricing, yields, and financial projections

import { CropType, PlotFinancialData } from '../types'

/**
 * AGC: Mock prices per crop type (in R$ per saca or tonelada)
 */
const MOCK_PRICES: Record<CropType, { price: number; unit: 'saca' | 'tonelada' }> = {
  soja: { price: 150.00, unit: 'saca' }, // AGC: R$ 150/saca (60kg)
  milho: { price: 85.00, unit: 'saca' }, // AGC: R$ 85/saca (60kg)
  trigo: { price: 95.00, unit: 'saca' }, // AGC: R$ 95/saca
  algodao: { price: 2800.00, unit: 'tonelada' }, // AGC: R$ 2800/ton
  cafe: { price: 1200.00, unit: 'saca' }, // AGC: R$ 1200/saca
  cana: { price: 180.00, unit: 'tonelada' }, // AGC: R$ 180/ton
  outros: { price: 100.00, unit: 'saca' }, // AGC: R$ 100/saca
}

/**
 * AGC: Mock projected yields per crop type (sacas/ha or tons/ha)
 */
const MOCK_YIELDS: Record<CropType, number> = {
  soja: 60, // AGC: 60 sacas/ha
  milho: 150, // AGC: 150 sacas/ha
  trigo: 45, // AGC: 45 sacas/ha
  algodao: 4.2, // AGC: 4.2 tons/ha
  cafe: 35, // AGC: 35 sacas/ha
  cana: 85, // AGC: 85 tons/ha
  outros: 40, // AGC: 40 sacas/ha
}

/**
 * AGC: Mock costs per hectare per crop type (in R$)
 */
const MOCK_COSTS_PER_HA: Record<CropType, number> = {
  soja: 4500, // AGC: R$ 4.500/ha
  milho: 5200, // AGC: R$ 5.200/ha
  trigo: 3800, // AGC: R$ 3.800/ha
  algodao: 12000, // AGC: R$ 12.000/ha
  cafe: 8500, // AGC: R$ 8.500/ha
  cana: 6500, // AGC: R$ 6.500/ha
  outros: 4000, // AGC: R$ 4.000/ha
}

/**
 * AGC: Calculate financial data for a plot/lot
 * AGC: Handles custom/learned crop types by falling back to "outros" values
 */
export function calculatePlotFinancials(
  cropType: CropType | string,
  areaHectares: number,
  managementProgress: number = 0
): PlotFinancialData {
  // AGC: Fallback to "outros" for custom/unknown crop types
  const priceData = MOCK_PRICES[cropType as CropType] || MOCK_PRICES.outros
  const projectedYield = MOCK_YIELDS[cropType as CropType] || MOCK_YIELDS.outros
  const costPerHa = MOCK_COSTS_PER_HA[cropType as CropType] || MOCK_COSTS_PER_HA.outros

  // AGC: Calculate projected revenue
  const totalYield = projectedYield * areaHectares
  const projectedRevenue = totalYield * priceData.price

  // AGC: Calculate projected and executed costs
  const projectedCost = costPerHa * areaHectares
  const executedCost = projectedCost * (managementProgress / 100)
  const executedCostPercentage = managementProgress

  return {
    projectedRevenue,
    projectedCost,
    executedCost,
    executedCostPercentage,
    currentPrice: priceData.price,
    projectedYield,
    priceUnit: priceData.unit,
  }
}

/**
 * AGC: Format currency to Brazilian Real
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

/**
 * AGC: Format yield with unit
 */
export function formatYield(value: number, unit: 'saca' | 'tonelada'): string {
  const unitStr = unit === 'saca' ? 'sc/ha' : 't/ha'
  return `${value.toFixed(1)} ${unitStr}`
}

/**
 * AGC: Get crop price info (for display)
 * AGC: Handles custom/learned crop types by falling back to "outros" values
 */
export function getCropPrice(cropType: CropType | string): string {
  // AGC: Fallback to "outros" for custom/unknown crop types
  const priceData = MOCK_PRICES[cropType as CropType] || MOCK_PRICES.outros
  const unitStr = priceData.unit === 'saca' ? 'sc' : 't'
  return `${formatCurrency(priceData.price)}/${unitStr}`
}


